// Account detail header — FE-039.

import { ArrowLeft } from "lucide-react";
import { Link, useLocation } from "react-router-dom";

import { useOptionalCommitmentModal } from "@/features/commitments";
import { RiskBadge } from "@/features/dashboard/accountPerformance";

import { AccountStatusBadge } from "./AccountStatusBadge";
import { formatPlanYearDate } from "./dateFormat";
import type { AccountDetail } from "./types";


interface AccountDetailHeaderProps {
  account: AccountDetail;
}


export function AccountDetailHeader({ account }: AccountDetailHeaderProps) {
  return (
    <header
      data-testid="account-detail-header"
      className="flex flex-col gap-3"
    >
      <Link
        to="/dashboard/accounts"
        data-testid="account-detail-back"
        className="inline-flex w-fit items-center gap-1 text-xs font-medium text-text-secondary hover:text-text-primary"
      >
        <ArrowLeft size={12} aria-hidden="true" />
        Back to Accounts
      </Link>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h1
              data-testid="account-detail-name"
              className="text-lg font-semibold tracking-tight text-text-primary"
            >
              {account.legal_name}
            </h1>
            {account.dba_name && (
              <span
                data-testid="account-detail-dba"
                className="text-sm text-text-tertiary"
              >
                ({account.dba_name})
              </span>
            )}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-text-secondary">
            <AccountStatusBadge status={account.status} />
            {account.risk_bucket && <RiskBadge bucket={account.risk_bucket} />}
            {account.renewal_date && (
              <span data-testid="account-detail-renewal">
                Renews {formatDate(account.renewal_date)}
              </span>
            )}
            {account.industry && (
              <span data-testid="account-detail-industry">
                · {account.industry}
              </span>
            )}
          </div>
        </div>
        <HeaderActions accountId={account.account_id} />
      </div>
    </header>
  );
}


function HeaderActions({ accountId }: { accountId: string }) {
  // FE-043c: + New Commitment opens the modal seeded with this
  // Account's id when the global modal provider is mounted; falls
  // back to the legacy ``/dashboard/commitments/new?account_id=…``
  // route otherwise.
  const commitmentModal = useOptionalCommitmentModal();
  const location = useLocation();
  return (
    <div
      className="flex flex-wrap items-center gap-2"
      data-testid="account-detail-actions"
    >
      {commitmentModal ? (
        <button
          type="button"
          data-testid="account-detail-new-commitment"
          onClick={() =>
            commitmentModal.openCreateCommitmentModal({
              defaults: { account_id: accountId },
              returnContext: {
                fromUrl: `${location.pathname}${location.search}`,
                fromLabel: "Back to Account",
              },
            })
          }
          className="rounded-md bg-teal px-3 py-1.5 text-sm font-medium text-text-inverse transition-fast ease-out hover:opacity-90"
        >
          + New Commitment
        </button>
      ) : (
        <Link
          to={`/dashboard/commitments/new?account_id=${encodeURIComponent(accountId)}`}
          data-testid="account-detail-new-commitment"
          className="rounded-md bg-teal px-3 py-1.5 text-sm font-medium text-text-inverse transition-fast ease-out hover:opacity-90"
        >
          + New Commitment
        </Link>
      )}
      <Link
        to={`/dashboard/commitment-tracker?account_id=${encodeURIComponent(accountId)}`}
        data-testid="account-overview-view-commitments"
        className="rounded-md border border-border-subtle bg-raised px-3 py-1.5 text-sm text-text-primary transition-fast ease-out hover:bg-recessed"
      >
        View Account Commitments
      </Link>
      <Link
        to={`/dashboard/accounts/${accountId}/edit`}
        data-testid="account-detail-edit"
        className="rounded-md border border-border-subtle bg-raised px-3 py-1.5 text-sm text-text-primary transition-fast ease-out hover:bg-recessed"
      >
        Edit Account
      </Link>
    </div>
  );
}


function formatDate(iso: string): string {
  // ``renewal_date`` arrives as YYYY-MM-DD — share the TZ-safe
  // formatter so a stored "2027-01-01" doesn't render as
  // "Dec 31, 2026" west of UTC.
  return formatPlanYearDate(iso);
}
